import React, { useCallback } from 'react';

import { Breadcrumb, message } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { AssetDescription } from '@/shared/ui/AssetDescription';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { Icon } from '@/shared/ui/Icon';
import { IconButton } from '@/shared/ui/IconButton';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './ContentHeader.module.css';

/**
 * 아이콘 설정
 */
export type IconConfig = {
  /** 아이콘 컴포넌트 */
  component: React.ReactNode;
  /** 아이콘 wrapper 커스텀 스타일 (선택적) */
  wrapperStyle?: React.CSSProperties;
  /** 아이콘 호버 시 표시할 툴팁 (선택적) */
  tooltip?: string;
};

/**
 * 콘텐츠 영역의 공통 헤더 컴포넌트
 *
 * @description
 * 상세 페이지, 카테고리 페이지 등의 메인 콘텐츠 영역 헤더를 표시합니다.
 * 아이콘, 제목, 설명, 액션 버튼 등을 일관된 레이아웃으로 제공합니다.
 *
 * @example
 * ```tsx
 * <ContentHeader
 *   icon={{
 *     component: <BookOutlined />,
 *     tooltip: "Glossary Term"
 *   }}
 *   title="용어 이름"
 *   description="용어 설명"
 *   titleRowExtra={<Tag>시스템 태그</Tag>}
 *   actions={<Button>하위 용어 추가</Button>}
 * />
 * ```
 */
export type ContentHeaderProps = {
  /** 브레드크럼 아이템 (선택적) */
  breadcrumbItems?: BreadcrumbItemType[];
  /** 헤더 아이콘 설정 (선택적) */
  icon?: IconConfig;
  /** 제목 (문자열 또는 React 요소) */
  title: string | React.ReactNode;
  /** 설명 (선택적) */
  description?: string | null;
  /** 제목 행에 추가될 요소 (태그, 배지 등) */
  titleRowExtra?: React.ReactNode;
  /** 오른쪽 액션 영역 */
  actions?: React.ReactNode;
  /** URL 복사 버튼 표시 여부 (기본값: true) */
  showCopyButton?: boolean;
  /** 커스텀 복사 핸들러 (기본: 현재 URL 복사) */
  onCopy?: () => void | Promise<void>;
  /** 헤더 컨테이너 커스텀 클래스명 */
  className?: string;
  /** 설명 편집 가능 여부 */
  descriptionEditable?: boolean;
  /** 설명 편집 버튼 클릭 핸들러 */
  onDescriptionEdit?: () => void;
};

export const ContentHeader = React.memo(
  ({
    breadcrumbItems,
    icon,
    title,
    description,
    titleRowExtra,
    actions,
    showCopyButton = true,
    onCopy,
    className,
    descriptionEditable = false,
    onDescriptionEdit,
  }: ContentHeaderProps) => {
    const handleCopy = useCallback(async () => {
      try {
        if (onCopy) {
          await onCopy();
        } else {
          await navigator.clipboard.writeText(window.location.href);
          message.success('복사되었습니다');
        }
      } catch {
        message.error('복사에 실패했습니다');
      }
    }, [onCopy]);

    const iconElement = icon ? (
      <div className="iconWrapper" style={icon.wrapperStyle}>
        {icon.component}
      </div>
    ) : null;

    return (
      <div className={className || styles.header}>
        <div className={styles.headerCard}>
          <div className={styles.leftContent}>
            {iconElement && icon?.tooltip ? (
              <Tooltip title={icon.tooltip} placement="bottom" arrow={false}>
                {iconElement}
              </Tooltip>
            ) : (
              iconElement
            )}
            <div className={styles.titleInfo}>
              <div className={styles.titleContent}>
                {breadcrumbItems && (
                  <div className={styles.breadcrumbWrapper}>
                    <Breadcrumb items={breadcrumbItems} />
                  </div>
                )}
                <div className={styles.titleRow}>
                  {typeof title === 'string' ? (
                    <h1 className={styles.title}>
                      <EllipsisText text={title} tooltipPlacement="topLeft" />
                    </h1>
                  ) : (
                    <div className={styles.title}>{title}</div>
                  )}
                  {showCopyButton && (
                    <IconButton onClick={handleCopy} tooltip="복사" tooltipPlacement="bottom">
                      <Icon name="copy" size={16} />
                    </IconButton>
                  )}
                  {titleRowExtra}
                </div>
              </div>
              <AssetDescription
                description={description ?? undefined}
                editable={descriptionEditable}
                onEdit={onDescriptionEdit}
              />
            </div>
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      </div>
    );
  }
);

ContentHeader.displayName = 'ContentHeader';
